import { inject, Injectable } from '@angular/core';
import {
  DeviceUsageType,
  DeviceUsageTypeV2,
  IgnoredEntry,
  JobState,
  JobStatus,
  LineUsageType,
  LineUsageTypeV2,
  MigrationStatus,
  MigrationStatusV2,
} from '../../shared/models/generated/ziroModels';
import { ZiroIcons } from '../../shared/models/ziro-icons.enum';
import { TranslateService } from '@ngx-translate/core';
import { DateAgoPipe } from '../../shared/pipes/date-ago.pipe';
import {
  BottomNavService,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../shared/bottom-nav/bottom-nav.service';
import { BottomNavButtonState } from '../../shared/bottom-nav/bottom-nav.component';

export type UILineType = {
  [K in keyof typeof LineUsageType | keyof typeof LineUsageTypeV2]: string;
};

export type UIDeviceType = {
  [K in keyof typeof DeviceUsageType]: string;
};

export const migrationsNumberSortFn = (a: string, b: string): number => {
  const aFormatted = a.replace(/[+\-\s]/g, '');
  const bFormatted = b.replace(/[+\-\s]/g, '');

  return parseInt(aFormatted) - parseInt(bFormatted);
};

export const UiMigrationStatus = {
  IGNORED: 'Ignored',
  MIGRATED: 'Migrated',
  NOT_READY: 'Not Ready',
  READY: 'Ready',
  STAGED: 'Staged',
};

export const UiLineType: UILineType = {
  ANALOG: 'Analog',
  INACTIVE: 'Inactive',
  MULTI_LINE_USER: 'Multi-Line User',
  PUBLIC_PHONE: 'Public Phone',
  SHARED_LINE: 'Shared Line',
  UCCX_AGENT: 'UCCX Agent',
  UNCLASSIFIED: 'Unclassified',
  USER: 'User',
  VOICE_APP: 'Voice Application',
  ORPHAN: 'Orphaned',
  AUTO_ATTENDANT: 'Auto Attendant',
  CONTACT_CENTER_AGENT: 'Contact Center Agent',
  SHARED_DEVICE: 'Shared Device',
};

export const UiDeviceType: UIDeviceType = {
  HOT_DESKING: 'Hot Desking',
  SOFTPHONE_ONLY: 'Softphone Only',
  DESKPHONE: 'Deskphone',
  CTI_PORT: 'CTI Port',
  ANALOG_FAX: 'Analog Fax',
  ANALOG_MODEM: 'Analog Modem',
  ANALOG_OTHER: 'Analog Other',
  MIXED: 'Unclassified',
  NO_DEVICE: 'No Device',
};

export enum UiSuggestedCallingProduct {
  SHARED_CALLING = 'Shared Calling',
  PER_MINUTE = 'Per Minute Calling',
  BUNDLED_MINUTES = 'Bundled Minutes',
  NOT_APPLICABLE = '',
}

export const COLOR_PALETTE = {
  primary: '#52D8DE',
  black: '#202020',
  yellow: '#FFCA5D',
  navy: '#001F3F',
  mint: '#A8E5A0',
  lavender: '#DD7EC2',
  slate: '#6A7B9D',
  orange: '#FFA85D',
  lightCoral: '#FFB2A1',
  teal: '#009688',
  gray: '#6c757d',
  success: '#42d740',
  danger: '#fb4656',
  msTeamsPurple: '#7f85f5',
  lighterMsTeamsPurple: '#a9adf0',
} as const;

@Injectable({ providedIn: 'root' })
export class MigrationsService {
  private readonly _translateService = inject(TranslateService);
  private readonly _dateAgoPipe = inject(DateAgoPipe);
  private readonly _bottomNavService = inject(BottomNavService);

  static readonly LINE_TYPE_ROW_MAP = new Map<
    LineUsageType | LineUsageTypeV2,
    { initialSortOrder: number; icon: ZiroIcons; iconClass?: string; lineType: string }
  >([
    [
      LineUsageType.USER,
      { initialSortOrder: 1, icon: ZiroIcons.CIRCLE_USER, iconClass: 'text-secondary', lineType: UiLineType.USER },
    ],
    [
      LineUsageType.PUBLIC_PHONE,
      {
        initialSortOrder: 2,
        icon: ZiroIcons.DESKPHONE_SMALL,
        iconClass: 'text-secondary',
        lineType: UiLineType.PUBLIC_PHONE,
      },
    ],
    [
      LineUsageType.UCCX_AGENT,
      {
        initialSortOrder: 3,
        icon: ZiroIcons.AGENT_SMALL,
        iconClass: 'text-secondary',
        lineType: UiLineType.UCCX_AGENT,
      },
    ],
    [
      LineUsageType.SHARED_LINE,
      {
        initialSortOrder: 4,
        icon: ZiroIcons.SHARED_LINE_SMALL,
        iconClass: 'text-secondary',
        lineType: UiLineType.SHARED_LINE,
      },
    ],
    [
      LineUsageType.MULTI_LINE_USER,
      {
        initialSortOrder: 5,
        icon: ZiroIcons.MULTI_LINE_USER_SMALL,
        iconClass: 'text-secondary',
        lineType: UiLineType.MULTI_LINE_USER,
      },
    ],
    [
      LineUsageType.VOICE_APP,
      {
        initialSortOrder: 6,
        icon: ZiroIcons.VOICE_APP_LINE_SMALL,
        iconClass: 'text-secondary',
        lineType: UiLineType.VOICE_APP,
      },
    ],
    [
      LineUsageType.ANALOG,
      {
        initialSortOrder: 7,
        icon: ZiroIcons.ANALOG_LINE_SMALL,
        iconClass: 'text-secondary',
        lineType: UiLineType.ANALOG,
      },
    ],
    [
      LineUsageType.INACTIVE,
      {
        initialSortOrder: 8,
        icon: ZiroIcons.INACTIVE_LINE_SMALL,
        iconClass: 'text-secondary',
        lineType: UiLineType.INACTIVE,
      },
    ],
    [
      LineUsageType.AUTO_ATTENDANT,
      {
        initialSortOrder: 9,
        icon: ZiroIcons.AUTO_ATTENDANT,
        iconClass: 'text-secondary',
        lineType: UiLineType.AUTO_ATTENDANT,
      },
    ],
    [
      LineUsageTypeV2.CONTACT_CENTER_AGENT,
      {
        initialSortOrder: 10,
        icon: ZiroIcons.AGENT_SMALL,
        iconClass: 'text-secondary',
        lineType: UiLineType.CONTACT_CENTER_AGENT,
      },
    ],
    [
      LineUsageTypeV2.SHARED_DEVICE,
      {
        initialSortOrder: 11,
        icon: ZiroIcons.SHARED_DEVICE,
        iconClass: 'text-secondary',
        lineType: UiLineType.SHARED_DEVICE,
      },
    ],
    [
      LineUsageType.ORPHAN,
      { initialSortOrder: 11, icon: ZiroIcons.ORPHAN, iconClass: 'text-dark', lineType: UiLineType.ORPHAN },
    ],
    [
      LineUsageType.UNCLASSIFIED,
      {
        initialSortOrder: 11,
        icon: ZiroIcons.UNDEFINED,
        iconClass: 'text-warning',
        lineType: UiLineType.UNCLASSIFIED,
      },
    ],
  ]);

  static readonly DEVICE_TYPE_ROW_MAP: Map<DeviceUsageType | DeviceUsageTypeV2, { label: string; icon: ZiroIcons }> =
    new Map<DeviceUsageType, { label: string; icon: ZiroIcons }>([
      [
        DeviceUsageType.SOFTPHONE_ONLY,
        {
          label: UiDeviceType.SOFTPHONE_ONLY,
          icon: ZiroIcons.SOFTPHONE_SMALL,
        },
      ],
      [
        DeviceUsageType.DESKPHONE,
        {
          label: UiDeviceType.DESKPHONE,
          icon: ZiroIcons.DESKPHONE_SMALL,
        },
      ],
      [
        DeviceUsageType.HOT_DESKING,
        {
          label: UiDeviceType.HOT_DESKING,
          icon: ZiroIcons.HOTDESK_SMALL,
        },
      ],
      [
        DeviceUsageType.CTI_PORT,
        {
          label: UiDeviceType.CTI_PORT,
          icon: ZiroIcons.CTI_PORT_LINE_SMALL,
        },
      ],
      [
        DeviceUsageType.ANALOG_FAX,
        {
          label: UiDeviceType.ANALOG_FAX,
          icon: ZiroIcons.FAX_LINE_SMALL,
        },
      ],
      [
        DeviceUsageType.ANALOG_MODEM,
        {
          label: UiDeviceType.ANALOG_MODEM,
          icon: ZiroIcons.MODEM_LINE_SMALL,
        },
      ],
      [
        DeviceUsageType.ANALOG_OTHER,
        {
          label: UiDeviceType.ANALOG_OTHER,
          icon: ZiroIcons.ANALOG_PHONE_SMALL,
        },
      ],
      [
        DeviceUsageType.MIXED,
        {
          label: UiDeviceType.MIXED,
          icon: ZiroIcons.MIXED_DEVICES_SMALL,
        },
      ],
      [
        DeviceUsageType.NO_DEVICE,
        {
          label: UiDeviceType.NO_DEVICE,
          icon: ZiroIcons.NO_DEVICES_SMALL,
        },
      ],
    ]);

  static readonly MIGRATION_STATUS_ROW_MAP = new Map<
    MigrationStatus | MigrationStatusV2,
    { label: string; iconColor: string }
  >([
    [MigrationStatus.IGNORED, { iconColor: COLOR_PALETTE.gray, label: UiMigrationStatus.IGNORED }],
    [MigrationStatus.MIGRATED, { iconColor: COLOR_PALETTE.msTeamsPurple, label: UiMigrationStatus.MIGRATED }],
    [MigrationStatus.READY, { iconColor: COLOR_PALETTE.success, label: UiMigrationStatus.READY }],
    [MigrationStatus.NOT_READY, { iconColor: COLOR_PALETTE.danger, label: UiMigrationStatus.NOT_READY }],
    [MigrationStatus.STAGED, { iconColor: COLOR_PALETTE.lighterMsTeamsPurple, label: UiMigrationStatus.STAGED }],
  ]);

  static shouldGetDnReport(dnsJobStatus: JobStatus, huntGroupJobStatus: JobStatus): boolean {
    return (
      dnsJobStatus.jobState === JobState.IDLE &&
      dnsJobStatus.lastRunTime !== null &&
      MigrationsService.isHuntGroupIdle(huntGroupJobStatus)
    );
  }

  static isHuntGroupIdle(huntGroupJobStatus: JobStatus): boolean {
    return huntGroupJobStatus.jobState === JobState.IDLE && huntGroupJobStatus.lastRunTime !== null;
  }

  static isErroredStatus(dnsJobStatus: JobStatus, huntGroupsJobStatus: JobStatus): boolean {
    return dnsJobStatus.error !== null || MigrationsService.isErroredHuntGroupStatus(huntGroupsJobStatus);
  }

  static isErroredHuntGroupStatus(huntGroupsJobStatus: JobStatus): boolean {
    return huntGroupsJobStatus.error !== null;
  }

  static isAutoAttendantReportIdle(autoAttendantJobStatus: JobStatus): boolean {
    return autoAttendantJobStatus.jobState === JobState.IDLE && autoAttendantJobStatus.lastRunTime !== null;
  }

  setBottomNavTimestamp(timestamp: string, translationKey: string): void {
    if (!timestamp) {
      return;
    }

    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: `<i class="${ZiroIcons.TIMESTAMP}"></i>
          ${this._translateService.instant(translationKey)} <strong>${this._dateAgoPipe.transform(timestamp)}</strong>`,
      }),
    );
  }

  dispatchButtonState(buttonId: string, state: BottomNavButtonState): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: buttonId,
        state: {
          pending: state.pending,
          buttonDisableState: {
            disabled: state.buttonDisableState.disabled,
            tooltipKey: state.buttonDisableState.tooltipKey,
          },
        },
      }),
    );
  }

  static buildIgnoredEntriesList(
    data: { migrationStatus?: string; id?: string }[],
    selectedRows: { id?: string }[],
    allSelectedRowsAreIgnored: boolean,
  ): IgnoredEntry[] {
    const currentIgnored = data
      ?.filter((row) => row.migrationStatus?.toUpperCase() === MigrationStatusV2.IGNORED)
      .map((row) => ({ rowId: row.id, ignoreReasonDetails: '' }));

    if (allSelectedRowsAreIgnored) {
      return currentIgnored.filter((ign) => !selectedRows.some((sel) => sel.id === ign.rowId));
    }

    const toAdd = selectedRows
      .filter((row) => !currentIgnored.some((ign) => ign.rowId === row.id))
      .map((row) => ({ rowId: row.id, ignoreReasonDetails: '' }));
    return [...currentIgnored, ...toAdd];
  }

  static deselectAllRows<T>(data: T[]): T[] {
    return data.map((row) => ({ ...row, isSelectedInTable: false }));
  }

  static getSelectedRows<T extends { isSelectedInTable?: boolean }>(data: T[]): T[] {
    return data?.filter((row) => row.isSelectedInTable) ?? [];
  }

  static areAllSelectedRowsIgnored(selectedRows: { migrationStatus?: string }[]): boolean {
    return selectedRows.every((row) => row.migrationStatus?.toUpperCase() === MigrationStatusV2.IGNORED);
  }

  static isAnySelectedRowIgnored(selectedRows: { migrationStatus?: string }[]): boolean {
    return selectedRows.some((row) => row.migrationStatus?.toUpperCase() === MigrationStatusV2.IGNORED);
  }

  getMigrationStatusHtml(value: string, isPending: boolean): string {
    if (!value) {
      return '';
    }

    if (isPending) {
      return `
        <span class="alert alert-inline alert-info">
          <i class="${ZiroIcons.LOADING} text-warning"></i>
          <span>${this._translateService.instant('tkey;migrations.number_report.table.migration_status.pending.label')}</span>
        </span>
      `;
    }

    const iconColor = Array.from(MigrationsService.MIGRATION_STATUS_ROW_MAP.values()).find(
      (val) => val.label === value,
    ).iconColor;

    if (value === MigrationsService.MIGRATION_STATUS_ROW_MAP.get(MigrationStatusV2.STAGED).label) {
      return `
        <span class="alert alert-inline alert-info">
          <i class="${ZiroIcons.CIRCLE_OUTLINE} fa-2xs" style="color: ${COLOR_PALETTE.msTeamsPurple};"></i>
          <span>${value}</span>
        </span>
      `;
    }

    return `
      <span class="alert alert-inline alert-info">
        <i class="${ZiroIcons.CIRCLE} fa-2xs" style="color: ${iconColor};"></i>
        <span>${value}</span>
      </span>
    `;
  }
}
